/**
 * Reading and rewriting the dealer the nav is pointed at (WARP-920).
 *
 * The URL is the only record of which dealer you are on — there is no selected-dealer
 * store. Two people on the same link see the same thing, the back button works, and
 * every page keeps reading `useParams` the way it already does.
 *
 * Kept free of React so it can be unit-tested on its own.
 */

// Matches `/dealer/<id>/…`, `/ro-intake/<id>`, and `/work-queue/<id>`. Loose on the id:
// WARP uses UUIDs, but the nav should not be the thing that breaks if that ever stops
// being true.
const DEALER_IN_PATH = /^\/(?:dealer|ro-intake|work-queue)\/([^/]+)/

/** The dealer this path is scoped to, or null for an all-dealers or global page. */
export const dealerIdFromPath = (pathname) => {
  const match = DEALER_IN_PATH.exec(pathname || '')
  return match ? match[1] : null
}

/**
 * Which nav area this path belongs to.
 *
 * Checked against the dealer form first: `/dealer/:id/intake/log` also starts with
 * `/dealer/:id`, so the longest match has to win or Log ROs would read as Outstanding
 * List. `startsWith` rather than equality, so a sub-route keeps its area lit.
 */
export const areaForPath = (pathname, options) => {
  const dealerId = dealerIdFromPath(pathname)

  const candidates = options
    .flatMap((option) => {
      const paths = []
      if (dealerId && option.dealerPath) paths.push(option.dealerPath(dealerId))
      // Pages that belong to the area without being its own address, like one RO's page.
      if (dealerId && option.alsoMatches) paths.push(...option.alsoMatches(dealerId))
      if (option.path) paths.push(option.path)
      // An all-dealers view the nav does not link to, but that is still this area, so a
      // dealer picked there lands on the area's page for that dealer.
      if (option.listPath) paths.push(option.listPath)
      return paths.map((path) => ({ option, path }))
    })
    .filter(({ path }) => pathname === path || pathname.startsWith(`${path}/`))

  if (candidates.length === 0) return null
  return candidates.sort((a, b) => b.path.length - a.path.length)[0].option
}

/**
 * Where picking `dealerId` should land: the same area, for the new dealer. `null` means
 * "All dealers", which is the area's list view.
 *
 * A dealer page the nav has no area for keeps its address with the dealer swapped.
 * Returns null when there is nowhere to go — a global area, or Log ROs with the dealer
 * cleared, since it has no list view.
 */
export const pathForDealer = (pathname, options, dealerId) => {
  const area = areaForPath(pathname, options)
  if (area && area.scope === 'dealer') {
    if (!dealerId) return area.path || null
    return area.dealerPath ? area.dealerPath(dealerId) : area.path || null
  }

  const current = dealerIdFromPath(pathname)
  if (current && dealerId) return pathname.replace(current, dealerId)
  return null
}

/** Where a nav item points, given the dealer currently in scope. */
export const pathForOption = (option, dealerId) => {
  if (option.scope !== 'dealer') return option.path
  if (dealerId && option.dealerPath) return option.dealerPath(dealerId)
  return option.path
}
